import { Box, Button, Tooltip } from "@mui/material";
import { Path } from "../interface";
import templatesStyles from "../Templates.styles";
import templatesTokens from "../Templates.tokens";
import { ChevronRightGlyph, HomeGlyph } from "./TemplatesIcons";

const styles = templatesStyles;
const { breadcrumb } = templatesTokens;

interface TemplateBreadcrumbProps {
  path: Path[];
  onBreadcrumbClick?: (index: number) => void;
  onHomeClick?: () => void;
  isLoading?: boolean;
  /** Accepted for call-site compatibility; the trail lays out the same either way. */
  isMobile?: boolean;
}

/**
 * Home ▸ Folder ▸ … trail drawn above the page title. The first crumb carries
 * the house glyph; every crumb but the last is an orange link.
 *
 * Below `md` the reference only draws the trail once a folder is open — at the
 * root the app bar already says "Templates" — and it scrolls sideways rather
 * than wrapping, with a plain "/" between crumbs on the phone.
 */
const TemplateBreadcrumb = ({
  path,
  onBreadcrumbClick,
  onHomeClick,
  isLoading = false,
}: TemplateBreadcrumbProps) => {
  const atRoot = path.length <= 1;

  return (
    <Box
      component="nav"
      aria-label="Breadcrumb"
      sx={styles.breadcrumbNav(atRoot)}
    >
      <Box
        component="ol"
        sx={{ ...styles.breadcrumb, listStyle: "none", m: 0, p: 0 }}
      >
        {path.map((crumb, index) => {
          const isLast = index === path.length - 1;
          const label =
            crumb.name.length > breadcrumb.maxLabelChars
              ? `${crumb.name.slice(0, breadcrumb.maxLabelChars)}…`
              : crumb.name;
          return (
            <Box
              component="li"
              key={crumb.id}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                flexShrink: 0,
                gap: { xs: "6px", sm: breadcrumb.gap },
              }}
            >
              {index > 0 && (
                <>
                  <Box component="span" aria-hidden sx={styles.breadcrumbSlash}>
                    /
                  </Box>
                  <ChevronRightGlyph
                    aria-hidden
                    sx={styles.breadcrumbSeparator}
                  />
                </>
              )}
              <Tooltip
                title={crumb.name}
                disableHoverListener={
                  crumb.name.length <= breadcrumb.maxLabelChars
                }
                arrow
              >
                <Button
                  disableRipple
                  disabled={isLoading}
                  aria-current={isLast ? "page" : undefined}
                  onClick={() =>
                    index === 0 ? onHomeClick?.() : onBreadcrumbClick?.(index)
                  }
                  sx={styles.breadcrumbItem(isLast, isLoading)}
                >
                  {index === 0 && (
                    <HomeGlyph
                      aria-hidden
                      sx={{
                        display: { xs: "none", md: "inline-block" },
                        width: breadcrumb.iconSize,
                        height: breadcrumb.iconSize,
                      }}
                    />
                  )}
                  {label}
                </Button>
              </Tooltip>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default TemplateBreadcrumb;
